import React, { useCallback, useEffect, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import classNames from 'classnames';

import SlideOver from '../../slide-over';
import ShareSlideoverContent from './content';
import { ShareSlideoverBreadcrumbs } from './action-bar/breadcrumbs';
import { ShareSlideoverBackButton } from './action-bar/back-button';
import ShareFormik from '../form/share.formik';
import ShareConfigurePanel from './configure.panel';

import { useShareThumbnailCodeStore } from '../thumbnail-code/store';
import { useSharePanelStore } from 'src/common/store/share.store';

import { Journey } from 'src/common/interfaces/journey.interface';
import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { Nullable } from 'src/types/nullable.type';

interface Props {
  onUpdateJourney: (journey: Journey) => void;
}

export const ShareSlideover = ({ onUpdateJourney }: Props) => {
  const {
    journey,
    isJourneySelected,
    onJourneyShareClose,
    isConfigureMode,
    resetSharePanel,
    linkConfigTree,
    setJourney,
    isSaving,
  } = useSharePanelStore(
    (state) => ({
      journey: state.sharedJourney,
      isJourneySelected: Boolean(state.sharedJourney),
      onJourneyShareClose: state.onJourneyShareClose,
      isConfigureMode: state.isConfigureMode,
      resetSharePanel: state.resetSharePanel,
      linkConfigTree: state.linkConfigTree,
      setJourney: state.setSharedJourney,
      isSaving: state.isSaving,
    }),
    shallow
  );
  const resetThumbnailElement = useShareThumbnailCodeStore((state) => state.resetThumbnailElement);
  const activePanelTitle = useMemo(() => {
    const slug = linkConfigTree.length ? linkConfigTree[linkConfigTree.length - 1].valueOf() : null;
    return !slug || ['additional-link'].includes(slug) ? null : slug;
  }, [linkConfigTree]);

  const updateJourney = useCallback(
    (params: any) => {
      const updatedJourney = { ...journey, ...params };
      onUpdateJourney(updatedJourney);
      setJourney(updatedJourney);
    },
    [journey, onUpdateJourney, setJourney]
  );

  // update the list of aliases, and only update thumbnail if parameter is passed (alias is primary)
  const onAliasUpdate = useCallback(
    (alias: JourneyAlias, thumbnailUrl: Nullable<string>) => {
      const aliasExists = journey ? journey?.aliases.find((a: JourneyAlias) => a.id === alias.id) : false;
      const aliases = aliasExists
        ? journey?.aliases.map((a: JourneyAlias) => {
            return a.id === alias.id ? alias : a;
          })
        : [...(journey?.aliases || []), alias];
      updateJourney(Object.assign({ aliases }, thumbnailUrl ? { thumbnail_url: thumbnailUrl } : null));
    },
    [journey, updateJourney]
  );

  const onClose = useCallback(() => {
    onJourneyShareClose();
    resetSharePanel();
  }, [onJourneyShareClose, resetSharePanel]);

  const actionBarItems = useMemo(
    () =>
      isConfigureMode ? (
        <div className='flex flex-row space-x-2'>
          <ShareSlideoverBackButton />
          <ShareSlideoverBreadcrumbs />
        </div>
      ) : null,
    [isConfigureMode]
  );

  const titleClassName = classNames('capitalize font-brand-serif', {
    'text-bedrock-h2': !isConfigureMode,
    'heading-bedrock-tertiary mt-2': isConfigureMode,
  });

  useEffect(() => {
    return resetThumbnailElement;
  }, [resetThumbnailElement]);

  if (!journey) return null;

  //  we are placing everything within the formik context so that different panels can directly access the context for values
  return (
    <SlideOver
      title={!isConfigureMode ? 'Link management' : activePanelTitle}
      type={'share'}
      open={isJourneySelected}
      maxWidth='auto'
      panelClassName='w-[368px]'
      titleClassName={titleClassName}
      onClose={onClose}
      containerClassName='z-neue-side-panel'
      actionLeftComponent={actionBarItems}
      showLoadingIndicator={isSaving}
    >
      {isConfigureMode ? (
        <ShareFormik onAliasUpdate={onAliasUpdate}>
          <ShareConfigurePanel journey={journey} />
        </ShareFormik>
      ) : (
        <ShareSlideoverContent onUpdateJourney={updateJourney} onAliasUpdate={onAliasUpdate} />
      )}
    </SlideOver>
  );
};
